<template>
  <div>
    <loading :active.sync="isLoading" :is-full-page="true" color="#4e97b2"></loading>
    <h1>Edit User</h1>
    <div class="container text-left">
      <div class="row justify-content-md-center">
        <div class="col-lg-12">
          <b-toast id="successToast" title="Success"> User has been saved. </b-toast>
          <b-card>
            <b-form @submit.prevent="onSubmit">
              <b-form-group label="First Name" label-for="firstName">
                <b-form-input id="firstName" v-model="user.firstName" placeholder="First Name" type="text" :state="submitted && $v.user.firstName.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.user.firstName.required">First Name is required</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Last Name" label-for="lastName">
                <b-form-input id="lastName" v-model="user.lastName" placeholder="Last Name" type="text" :state="submitted && $v.user.lastName.$error ? false : null" />
                <b-form-invalid-feedback v-if="submitted && !$v.user.lastName.required">Last Name is required</b-form-invalid-feedback>
              </b-form-group>

              <TelephoneInput v-model="user.telephone" label="Telephone" id="telephone" :submitted="submitted" />
              <div><b>Email:</b> {{ this.user.email }}</div>
              <br />

              <b-form-group label="Role" label-for="role" v-if="userRole === 'admin' || userRole === 'editor'">
                <b-form-select v-model="user.role" id="role" name="role">
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="limited_editor">Limited Editor</option>
                  <option value="dmo">DMO</option>
                  <option value="partner">Partner</option>
                </b-form-select>
              </b-form-group>

              <div class="org-list">Organizations</div>
              <div class="table-responsive">
                <table class="table table-sm table-striped">
                  <thead></thead>
                  <tbody>
                    <tr v-for="(org, index) in user.organizations" :key="index">
                      <td class="org-name">{{ org.name }}</td>
                      <td>
                        <b-button class="btn btn-secondary float-right" @click="removeUserFromOrg(user._id, org._id, index)">Remove</b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="userRole === 'admin' || userRole === 'editor'" class="row justify-content-md-center">
                <div class="col-lg-12 mt-3">
                  <div class="org-list">Search to Add Organizations</div>
                  <b-input-group class="org-list">
                    <b-input id="search" v-model="search" placeholder="Search" />
                    <b-input-group-append>
                      <b-button name="search" class="btn btn-secondary search" @click="searchForOrgs()">Search</b-button>
                      <b-button v-if="userHasSearched" name="search" class="ml-3" @click="clearSearch()">Clear Search</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </div>
              <div class="row text-left mt-3" v-if="$store.state.organizations.searchList.length > 0 && !userClearedSearch">
                <div class="col-lg-3" v-for="org in $store.state.organizations.searchList" :key="org._id">
                  <b-card v-match-heights="{ el: ['.org-card'] }" class="mb-3 org-card" header="Organization">
                    <b-button class="btn join-btn" :disabled="isLoadingOrgIds.includes(org._id)" @click="addUserToOrg(userId, org._id, org.name)">Add</b-button>
                    <router-link :to="{ name: 'org detail', params: { orgId: org._id, orgName: org.name } }" class="button"
                      ><b>{{ org.name }}</b></router-link
                    >
                    <br />
                    {{ org.address1 }} <br />
                    {{ org.city }}, AL {{ org.zip }} <br />
                    {{ org.telephone | telephoneNumber }} <br />
                  </b-card>
                </div>
              </div>
              <div v-else-if="$store.state.organizations.searchList.length == 0 && userHasSearched">
                <b-alert show variant="info" class="mt-3">No Results Found</b-alert>
              </div>

              <b-form-group class="change-password-group" label="Change Password" label-for="changePassword">
                <b-form-checkbox v-model="changePassword" id="changePassword" name="changePassword" />
              </b-form-group>

              <div v-if="changePassword">
                <b-form-group label="Old Password" label-for="oldPassword" v-if="$store.state.auth.user.role !== 'admin' && $store.state.auth.user.role !== 'editor'">
                  <b-form-input
                    id="oldPassword"
                    name="oldPassword"
                    v-model="oldPassword"
                    placeholder="Old Password"
                    type="password"
                    :state="submitted && $v.oldPassword.$error ? false : null"
                  />
                  <b-form-invalid-feedback v-if="submitted && !$v.oldPassword.required">Password is required</b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Password" label-for="password">
                  <b-form-input
                    id="password"
                    name="password"
                    v-model="user.password"
                    placeholder="Password"
                    type="password"
                    :state="submitted && $v.user.password.$error ? false : null"
                  />
                  <b-form-invalid-feedback v-if="submitted && !$v.user.password.required">Password is required</b-form-invalid-feedback>
                  <b-form-invalid-feedback v-if="submitted && !$v.user.password.minLength">Password needs to be at least 10 characters</b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Confirm Password" label-for="confirmPassword">
                  <b-form-input
                    id="confirmPassword"
                    name="confirmPassword"
                    v-model="user.confirmPassword"
                    placeholder="Confirm Password"
                    type="password"
                    :state="submitted && $v.user.confirmPassword.$error ? false : null"
                  />
                  <b-form-invalid-feedback v-if="submitted && !$v.user.confirmPassword.required">Confirm Password is required</b-form-invalid-feedback>
                  <b-form-invalid-feedback v-if="submitted && !$v.user.confirmPassword.sameAs"
                    >Confirm Password needs to be the same as the password entered above</b-form-invalid-feedback
                  >
                  <b-form-invalid-feedback v-if="submitted && !$v.user.confirmPassword.minLength">Confirm Password needs to be at least 10 characters</b-form-invalid-feedback>
                </b-form-group>
              </div>

              <b-form-group>
                <b-form-checkbox v-model="user.news" id="news" name="news" value="news">{{
                  user.news ? 'Signed-up for Industry News' : 'Sign up for Industry News'
                }}</b-form-checkbox>
              </b-form-group>

              <b-form-group v-if="(userRole === 'admin' && needsVerifying) || (userRole === 'editor' && needsVerifying)">
                <b-button id="verifyEmail" @click="verifyEmail()">Verify Email</b-button>
              </b-form-group>

              <div v-if="(userRole === 'admin' && needsConverting) || (userRole === 'editor' && needsConverting)">
                This account needs to be converted from the old system to the new system.
              </div>

              <b-button type="submit" :disabled="submitted && $v.$invalid">Save</b-button>

              <b-form-invalid-feedback v-if="processErrors">{{ processErrors }}</b-form-invalid-feedback>
            </b-form>
          </b-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import {
  email, minLength, required, requiredIf, sameAs
} from 'vuelidate/lib/validators';
import TelephoneInput from '@/components/TelephoneInput.vue';

export default {
  data() {
    return {
      isLoading: false,
      user: {
        firstName: '',
        lastName: '',
        telephone: '',
        email: '',
        password: '',
        confirmPassword: '',
        news: false,
        role: 'partner',
        organizations: [],
      },
      search: '',
      cognitoUser: {},
      submitted: false,
      processErrors: null,
      changePassword: false,
      oldPassword: '',
      userRole: '',
      needsConverting: false,
      needsVerifying: false,
      filter: '',
      userHasSearched: false,
      userClearedSearch: false,
      orgExists: false,
      isLoadingOrgIds: [],
    };
  },
  components: {
    Loading,
    TelephoneInput,
  },
  validations: {
    user: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      telephone: {
        required,
      },
      email: {
        required,
        email,
      },
      password: {
        minLength: minLength(10),
      },
      confirmPassword: {
        required: requiredIf((user) => user.password && user.password.length > 0),
        sameAs: sameAs('password'),
        minLength: minLength(10),
      },
    },
    oldPassword: {
      required: requiredIf((root) => root.userRole !== 'admin' && root.userRole !== 'editor' && root.user.password && root.user.password.length > 0),
    },
  },
  props: {
    userId: String,
    orgId: String,
    orgName: String,
  },
  async created() {
    this.isLoading = true;
    const user = await axios.get(`${process.env.VUE_APP_API_USERS_ROOT}/users/${this.userId}`);
    this.mapUser(user.data);
    this.userRole = this.$store.state.auth.user.role;

    if (this.userRole === 'admin' || this.userRole === 'editor') {
      // eslint-disable-next-line
      const response = await axios.post(`${process.env.VUE_APP_API_USERS_ROOT}/users/listUsers`, { filter: `email = \"${user.data.email}\"` });
      const users = response.data.Users;
      if (users.length <= 0) {
        this.needsConverting = true;
      } else {
        this.cognitoUser = { ...users[0] };
        this.needsVerifying = this.cognitoUser.UserStatus === 'UNCONFIRMED';
      }
    }
    this.isLoading = false;
  },
  beforeDestroy() {
    this.clearSearch();
    this.$store.dispatch('organizations/resetSearchList');
  },
  methods: {
    async onSubmit() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      const newUser = {
        email: this.user.email,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        telephone: this.user.telephone,
        news: this.user.news,
      };

      if (this.$store.state.auth.user.role === 'admin' || this.$store.state.auth.user.role === 'editor') {
        newUser.role = this.user.role;
      }

      if (this.changePassword) {
        if (this.needsConverting === true) {
          newUser.password = this.user.password;
          newUser.adminInitChangePassword = this.needsConverting;
          await this.$store.dispatch('auth/signUp', {
            username: this.user.email,
            password: this.user.password,
            needsConverting: this.needsConverting,
            attributes: {
              email: this.user.email,
              phone_number: `${this.user.telephone.replace(/^\d{10}$/g, '')}`,
              given_name: this.user.firstName,
              family_name: this.user.lastName,
            },
          });
        }
        if (this.$store.state.auth.user.role === 'admin' || this.$store.state.auth.user.role === 'editor') {
          // To api layer. handler admin.auth,adminChangePassword
          await axios
            .post(`${process.env.VUE_APP_API_USERS_ROOT}/users/changePassword`, {
              username: this.user.email,
              password: this.user.password,
              needsconverting: this.needsConverting,
            })
            .catch(() => {
              if (this.needsConverting) {
                this.$bvToast.toast('This account needs to be converted.', {
                  title: 'Error',
                  autoHideDelay: 5000,
                  appendToast: true,
                });
              } else {
                this.$bvToast.toast('Something went wrong. Please try again.', {
                  title: 'Error',
                  autoHideDelay: 5000,
                  appendToast: true,
                });
              }
            });
        } else {
          try {
            await this.$store.dispatch('auth/changePassword', { oldPassword: this.oldPassword, newPassword: this.user.password });
          } catch (err) {
            if (this.needsConverting) {
              this.$bvToast.toast('This account needs to be converted.', {
                title: 'Error',
                autoHideDelay: 5000,
                appendToast: true,
              });
            } else {
              this.$bvToast.toast('Something went wrong. Please try again.', {
                title: 'Error',
                autoHideDelay: 5000,
                appendToast: true,
              });
            }
          }
        }
      }
      await this.sendUserToServer();
    },
    async sendUserToServer() {
      const url = this.userId ? `${process.env.VUE_APP_API_USERS_ROOT}/users/${this.userId}` : `${process.env.VUE_APP_API_USERS_ROOT}/users`;
      try {
        const response = await axios.post(url, this.user);
        if (response.status === 200) {
          const self = this;
          if (this.userId || this.$store.state.auth.user.role === 'admin' || this.$store.state.auth.user.role === 'editor') {
            this.$router.push({ name: 'users detail', params: { userId: this.userId } }, () => {
              setTimeout(() => {
                self.$bvToast.toast('User has been saved', { title: 'Success', autoHideDelay: 5000, appendToast: true });
              }, 500);
            });
          } else {
            this.$router.push({ name: 'users list' }, () => {
              setTimeout(() => {
                self.$bvToast.toast('User has been saved', { title: 'Success', autoHideDelay: 5000, appendToast: true });
              }, 500);
            });
          }
        } else {
          this.$bvToast.toast('Error Saving User', {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }
      } catch (err) {
        console.log(err.toJSON());

        if (this.needsConverting) {
          this.$bvToast.toast('This account needs to be converted.', {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        } else {
          this.$bvToast.toast('Something went wrong. Please try again.', {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }
      }
    },
    async removeUserFromOrg(userId, orgId, index) {
      const url = `${process.env.VUE_APP_API_ORGANIZATIONS_ROOT}/removeUserFromOrg/${this.userId}/${orgId}`;
      this.user.organizations.splice(index, 1);
      this.isLoadingOrgIds = this.isLoadingOrgIds.filter(x => x !== orgId);
      const response = await axios.get(url);
      if (response.status === 200) {
        this.$bvToast.toast('You have successfully removed this organization from the user.', {
          title: 'Success',
          autoHideDelay: 5000,
          appendToast: true,
        });
      } else {
        this.$bvToast.toast(JSON.parse(response.data).message, {
          title: 'Error',
          autoHideDelay: 5000,
          appendToast: true,
        });
      }
    },
    mapUser(usr) {
      this.user = {
        firstName: usr.first_name,
        lastName: usr.last_name,
        email: usr.email,
        telephone: usr.telephone,
        news: usr.tourism_news_optin,
        organizations: usr.organizations,
        role: usr.role !== '' ? usr.role : 'partner',
      };

      this.user.organizations.map(x => this.isLoadingOrgIds.push(x._id));
    },
    async clearSearch() {
      this.search = '';
      this.userHasSearched = false;
      this.userClearedSearch = true;
    },
    compareValues(key, order = 'asc') {
      return function innerSort(a, b) {
        // eslint-disable-next-line
        if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
        const comparison = a[key].localeCompare(b[key]);

        return order === 'desc' ? comparison * -1 : comparison;
      };
    },
    sort(field) {
      if (this.sortingField === field) {
        this.sortingOrder = this.sortingOrder === 'asc' ? 'desc' : 'asc';
      }
      this.sortingField = field;
      this.userOrgs.sort(this.compareValues(this.sortingField, this.sortingOrder));
      this.sortField = this.displayMap[this.sortingField];
    },
    searchList() {
      this.$store.dispatch('organizations/setFilter', this.filter);
    },
    async searchForOrgs() {
      await this.$store.dispatch('organizations/searchOrgsToJoin', this.search);
      this.userClearedSearch = false;
      this.userHasSearched = true;
    },
    async addUserToOrg(userId, orgId, orgName) {
      const url = `${process.env.VUE_APP_API_ORGANIZATIONS_ROOT}/adminAddUserToOrg/${this.userId}/${orgId}`;
      this.isLoadingOrgIds.push(orgId);
      const response = await axios.get(url);
      if (response.status === 200) {
        this.$bvToast.toast('You have successfully added this user to the organization.', {
          title: 'Success',
          autoHideDelay: 5000,
          appendToast: true,
        });
      } else {
        this.$bvToast.toast(JSON.parse(response.data).message, {
          title: 'Error',
          autoHideDelay: 5000,
          appendToast: true,
        });
        this.isLoadingOrgIds = this.isLoadingOrgIds.filter(x => x !== orgId);
      }
      const addedOrg = { name: orgName, _id: orgId };
      if (this.user.organizations.map((o) => o._id).includes(orgId)) {
        this.orgExists = true;
      } else {
        this.user.organizations.push(addedOrg);
      }
    },
  },
};
</script>

<style scoped>
.change-password-group >>> div {
  float: left;
  padding-left: 1.5rem;
}

.change-password-group >>> label {
  display: inline !important;
}

.change-password-group >>> .custom-control {
  padding-left: 0px;
}

.card {
  border: none;
}

.join-btn {
  position: absolute;
  top: 10px;
  right: 10px;
}

.org-list {
  margin-bottom: 0.5rem;
}

.org-name {
  font-size: 1rem;
}
</style>
